import Cookies from "js-cookie";

import { ConsentGroup, hasConsent, type ConsentState } from "@/lib/consent";

/** Campaign-attribution params. Session cookie (cleared on browser close). */
export const UTM_COOKIE = "bsky_utm";

/** The `utm_*` params marketing uses, without the `utm_` prefix. */
export const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

/** Keep values bounded to avoid junk/oversized cookies. */
const MAX_VALUE_LENGTH = 255;

function sanitizeValue(raw: string | null): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

/** Read only the known `utm_*` params from a query string, sanitized. */
export function readUtmFromUrl(search: string = typeof window !== "undefined" ? window.location.search : ""): UtmParams {
  const params = new URLSearchParams(search);
  const utm: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = sanitizeValue(params.get(`utm_${key}`));
    if (value) utm[key] = value;
  }
  return utm;
}

/** The currently stored UTM params, or null when absent/malformed. */
export function getStoredUtm(): UtmParams | null {
  const raw = Cookies.get(UTM_COOKIE);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    const utm: UtmParams = {};
    for (const key of UTM_KEYS) {
      if (typeof record[key] === "string") utm[key] = record[key] as string;
    }
    return Object.keys(utm).length ? utm : null;
  } catch {
    return null;
  }
}

/**
 * First-touch capture: write the URL's `utm_*` into a session cookie only when
 * Targeting consent is granted, params are present, and nothing is stored yet.
 * Safe to call on every public page load — a no-op unless all three hold.
 */
export function captureUtm(search?: string): void {
  if (!hasConsent(ConsentGroup.Targeting)) return;
  if (getStoredUtm()) return; // first-touch: never overwrite an existing capture
  const utm = readUtmFromUrl(search);
  if (!Object.keys(utm).length) return;
  Cookies.set(UTM_COOKIE, JSON.stringify(utm), {
    // No `expires` → session cookie, cleared when the browser closes.
    sameSite: "lax",
    secure: typeof window !== "undefined" && window.location.protocol === "https:",
    path: "/",
  });
}

/**
 * Withdrawing Targeting has to remove what capture already wrote, not just stop
 * capturing — otherwise the attribution cookie outlives the consent it was
 * stored under for the rest of the browser session. Driven from the consent
 * subscriber rather than `useUtmCapture`, so it also fires on the surfaces that
 * never capture but do offer Cookie Settings (console, admin, docs).
 */
export function syncUtmWithConsent(state: ConsentState): void {
  if (state.decided && state.granted.has(ConsentGroup.Targeting)) return;
  // Same `path` as the write, or the browser keeps the original cookie.
  Cookies.remove(UTM_COOKIE, { path: "/" });
}
